/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled, { css } from "styled-components";
import {
  CheckIcon,
  CheckSolidIcon,
  CheckCircleIcon,
  CheckSquareIcon,
  CrossIcon,
  ErrorIcon,
  AlertCircleIcon,
  ClockIcon,
  ClockCheckIcon,
  FilterIcon,
  UploadIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UserIcon,
  TrashIcon,
  LocationIcon,
  PhoneIcon,
  PlusIcon,
  NavigateLeftIcon,
  PencilAndPaperIcon,
  CalendarPlusIcon,
  CalendarDetailedIcon,
  CalendarCheckIcon,
  CalendarEmptyIcon,
  InfoCircleIcon,
  InfoSquareSolidIcon,
  EyeIcon,
  EyeCrossedIcon,
  RenewIcon,
  SelectServicesIcon,
  SelectProviderIcon,
  SelectDateTimeIcon,
  ServicePlaceholderIcon,
  LocationPlaceholderIcon,
  UsersIcon,
  SearchIcon,
  CreditCardIcon,
  LogOutIcon,
  LoadingIndicatorIcon,
  PrintIcon,
  PaymentProcessorDelayIcon,
  ShuffleIcon,
  FileListIcon,
  GiftIcon,
  GearIcon,
  BurgerIcon,
  EnvelopeEmailIcon,
  VideoIcon,
  OnlineMeetingIcon,
  KebabIcon,
  BlockIcon,
  ProductsIcon,
  MinusIcon,
  CartIcon,
  BoxArchiveIcon,
  BagIcon,
  DoubleArrowRightIcon,
  EmailIcon,
  CalendarCrossIcon,
  FilesIcon,
  PromotionIcon,
  GiftCardIcon,
  MembershipIcon,
  CardsStackIcon,
  MembershipItemIcon,
  RecurringChargeIcon,
  GoogleCalendarIcon,
  AppleCalendarIcon,
  OutlookCalendarIcon,
  YahooCalendarIcon,
  StorageBoxIcon,
  DownloadIcon,
  BlocksIcon,
  ClassPlaceholderIcon,
  SelectClassIcon,
} from "./const/icon";

const StyledIcon = styled(({ Icon, ...props }) => <Icon {...props} width={props.width || props.size} height={props.height || props.size} />)`
  display: block;
  ${(props) => colorStyles[props.$color] || props.$color || colorStyles.dark}
`;

const iconSize = {
  XS: "16px",
  SM: "20px",
  MD: "24px",
  LG: "32px",
  XL: "40px",
  XXL: "48px",
};

const getIconStrokeWidth = (size) => {
  const px = parseFloat(size);

  // if (!px || px <= 16) return 1;
  // if (!px || px <= 20) return 1.5;
  // if (px <= 32) return 2;

  return 1.5;
};

const colorStyles = {
  primary: css`
    stroke: rgba(${({ theme }) => theme.colors.accent.rgba});
    color: rgba(${({ theme }) => theme.colors.accent.rgba});
  `,
  secondary: css`
    stroke: rgba(${({ theme }) => theme.colors.textSecondary.rgba});
    color: rgba(${({ theme }) => theme.colors.textSecondary.rgba});
  `,
  dark: css`
    stroke: rgba(${({ theme }) => theme.colors.text.rgba});
    color: rgba(${({ theme }) => theme.colors.text.rgba});
  `,
  iconStroke: css`
    stroke: rgba(${({ theme }) => theme.colors.iconStroke.rgba});
    color: rgba(${({ theme }) => theme.colors.iconStroke.rgba});
  `,
  danger: css`
    stroke: rgba(${({ theme }) => theme.colors.error.rgba});
    color: rgba(${({ theme }) => theme.colors.error.rgba});
  `,
  success: css`
    stroke: rgba(${({ theme }) => theme.colors.success.rgba});
    color: rgba(${({ theme }) => theme.colors.success.rgba});
  `,
  warning: css`
    stroke: rgba(${({ theme }) => theme.colors.warning.rgba});
    color: rgba(${({ theme }) => theme.colors.warning.rgba});
  `,
};

const IconComponent = ({ Icon, size = iconSize.XS, color, ...props }) => (
  <StyledIcon Icon={Icon} size={size} $color={color} strokeWidth={getIconStrokeWidth(size)} {...props} />
);

const Icon = {
  Check: (props) => <IconComponent Icon={CheckIcon} {...props} />,
  CheckSolid: (props) => <IconComponent Icon={CheckSolidIcon} {...props} />,
  CheckCircle: (props) => <IconComponent Icon={CheckCircleIcon} {...props} />,
  CheckSquare: (props) => <IconComponent Icon={CheckSquareIcon} {...props} />,
  Cross: (props) => <IconComponent Icon={CrossIcon} {...props} />,
  Error: (props) => <IconComponent Icon={ErrorIcon} {...props} />,
  AlertCircle: (props) => <IconComponent Icon={AlertCircleIcon} {...props} />,
  Clock: (props) => <IconComponent Icon={ClockIcon} {...props} />,
  ClockCheck: (props) => <IconComponent Icon={ClockCheckIcon} {...props} />,
  Filter: (props) => <IconComponent Icon={FilterIcon} {...props} />,
  Upload: (props) => <IconComponent Icon={UploadIcon} {...props} />,
  ChevronUp: (props) => <IconComponent Icon={ChevronUpIcon} {...props} />,
  ChevronLeft: (props) => <IconComponent Icon={ChevronLeftIcon} {...props} />,
  ChevronRight: (props) => <IconComponent Icon={ChevronRightIcon} {...props} />,
  ChevronDown: (props) => <IconComponent Icon={ChevronDownIcon} {...props} />,
  User: (props) => <IconComponent Icon={UserIcon} {...props} />,
  Trash: (props) => <IconComponent Icon={TrashIcon} {...props} />,
  Location: (props) => <IconComponent Icon={LocationIcon} {...props} />,
  Phone: (props) => <IconComponent Icon={PhoneIcon} {...props} />,
  Plus: (props) => <IconComponent Icon={PlusIcon} {...props} />,
  Minus: (props) => <IconComponent Icon={MinusIcon} {...props} />,
  NavigateLeft: (props) => <IconComponent Icon={NavigateLeftIcon} {...props} />,
  PencilAndPaper: (props) => <IconComponent Icon={PencilAndPaperIcon} {...props} />,
  CalendarPlus: (props) => <IconComponent Icon={CalendarPlusIcon} {...props} />,
  CalendarDetailed: (props) => <IconComponent Icon={CalendarDetailedIcon} {...props} />,
  CalendarCheck: (props) => <IconComponent Icon={CalendarCheckIcon} {...props} />,
  CalendarEmpty: (props) => <IconComponent Icon={CalendarEmptyIcon} {...props} />,
  InfoCircle: (props) => <IconComponent Icon={InfoCircleIcon} {...props} />,
  InfoSquareSolidIcon: (props) => <IconComponent Icon={InfoSquareSolidIcon} {...props} />,
  Eye: (props) => <IconComponent Icon={EyeIcon} {...props} />,
  EyeCrossed: (props) => <IconComponent Icon={EyeCrossedIcon} {...props} />,
  Renew: (props) => <IconComponent Icon={RenewIcon} {...props} />,
  Users: (props) => <IconComponent Icon={UsersIcon} {...props} />,
  Shuffle: (props) => <IconComponent Icon={ShuffleIcon} {...props} />,
  Search: (props) => <IconComponent Icon={SearchIcon} {...props} />,
  CreditCard: (props) => <IconComponent Icon={CreditCardIcon} {...props} />,
  LogOut: (props) => <IconComponent Icon={LogOutIcon} {...props} />,
  Print: (props) => <IconComponent Icon={PrintIcon} {...props} />,
  Products: (props) => <IconComponent Icon={ProductsIcon} {...props} />,
  SelectServices: (props) => <IconComponent Icon={SelectServicesIcon} {...props} />,
  SelectProvider: (props) => <IconComponent Icon={SelectProviderIcon} {...props} />,
  SelectDateTime: (props) => <IconComponent Icon={SelectDateTimeIcon} {...props} />,
  SelectClass: (props) => <IconComponent Icon={SelectClassIcon} {...props} />,
  ServicePlaceholderIcon: (props) => <IconComponent Icon={ServicePlaceholderIcon} {...props} />,
  LocationPlaceholderIcon: (props) => <IconComponent Icon={LocationPlaceholderIcon} {...props} />,
  ClassPlaceholderIcon: (props) => <IconComponent Icon={ClassPlaceholderIcon} {...props} />,
  LoadingIndicator: (props) => <IconComponent Icon={LoadingIndicatorIcon} {...props} />,
  PaymentProcessorDelay: (props) => <IconComponent Icon={PaymentProcessorDelayIcon} {...props} />,
  FileList: (props) => <IconComponent Icon={FileListIcon} {...props} />,
  Gift: (props) => <IconComponent Icon={GiftIcon} {...props} />,
  Gear: (props) => <IconComponent Icon={GearIcon} {...props} />,
  Burger: (props) => <IconComponent Icon={BurgerIcon} {...props} />,
  Cart: (props) => <IconComponent Icon={CartIcon} {...props} />,
  EnvelopeEmailIcon: (props) => <IconComponent Icon={EnvelopeEmailIcon} {...props} />,
  VideoIcon: (props) => <IconComponent Icon={VideoIcon} {...props} />,
  OnlineMeetingIcon: (props) => <IconComponent Icon={OnlineMeetingIcon} {...props} />,
  KebabIcon: (props) => <IconComponent Icon={KebabIcon} {...props} />,
  BlockIcon: (props) => <IconComponent Icon={BlockIcon} {...props} />,
  BoxArchive: (props) => <IconComponent Icon={BoxArchiveIcon} {...props} />,
  Bag: (props) => <IconComponent Icon={BagIcon} {...props} />,
  DoubleArrowRight: (props) => <IconComponent Icon={DoubleArrowRightIcon} {...props} />,
  Email: (props) => <IconComponent Icon={EmailIcon} {...props} />,
  CalendarCross: (props) => <IconComponent Icon={CalendarCrossIcon} {...props} />,
  Files: (props) => <IconComponent Icon={FilesIcon} {...props} />,
  Promotion: (props) => <IconComponent Icon={PromotionIcon} {...props} />,
  GiftCard: (props) => <IconComponent Icon={GiftCardIcon} {...props} />,
  Membership: (props) => <IconComponent Icon={MembershipIcon} {...props} />,
  CardsStack: (props) => <IconComponent Icon={CardsStackIcon} {...props} />,
  MembershipItem: (props) => <IconComponent Icon={MembershipItemIcon} {...props} />,
  RecurringCharge: (props) => <IconComponent Icon={RecurringChargeIcon} {...props} />,
  GoogleCalendar: (props) => <IconComponent Icon={GoogleCalendarIcon} {...props} />,
  AppleCalendar: (props) => <IconComponent Icon={AppleCalendarIcon} {...props} />,
  OutlookCalendar: (props) => <IconComponent Icon={OutlookCalendarIcon} {...props} />,
  YahooCalendar: (props) => <IconComponent Icon={YahooCalendarIcon} {...props} />,
  Blocks: (props) => <IconComponent Icon={BlocksIcon} {...props} />,
  StorageBox: (props) => <IconComponent Icon={StorageBoxIcon} {...props} />,
  Download: (props) => <IconComponent Icon={DownloadIcon} {...props} />,
  SIZE: iconSize,
};

export default Icon;
